<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import './douyin.style.css'

/* ============================================================
 *  常量与响应式状态
 * ============================================================ */
const API_BASE = '/api/douyin'
const HISTORY_KEY = 'douyin_history'
const MAX_HISTORY = 20

const inputText = ref('')
const inputRef = ref(null)
const videoPlayerRef = ref(null)

// 状态
const loading = ref(false)
const loadingText = ref('正在解析视频信息...')
let loadingTimer = null

const currentResult = ref(null)
const errorMsg = ref('')
const showPreviewOverlay = ref(true)

// 下载进度
const downloadProgressVisible = ref(false)
const progressPercent = ref(0)
const progressText = ref('')

// 历史记录
const historyList = ref([])

// Toast
const toast = ref({
  visible: false,
  message: '',
  type: 'success',
})
let toastTimer = null

// 主题
const theme = ref('light')

/* ============================================================
 *  计算属性
 * ============================================================ */
const showResultSection = computed(() => currentResult.value || errorMsg.value)

const proxyVideoSrc = computed(() => {
  if (!currentResult.value?.download_url) return ''
  return API_BASE + '/download?url=' + encodeURIComponent(currentResult.value.download_url)
})

/* ============================================================
 *  返回首页
 * ============================================================ */
function goHome() {
  history.pushState({}, '', '/')
  window.dispatchEvent(new PopStateEvent('popstate'))
}

/* ============================================================
 *  生命周期
 * ============================================================ */
onMounted(() => {
  renderHistory()
  initTheme()
})

onUnmounted(() => {
  if (loadingTimer) clearInterval(loadingTimer)
  if (toastTimer) clearTimeout(toastTimer)
})

function initTheme() {
  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  const apply = () => { theme.value = mq.matches ? 'dark' : 'light' }
  apply()
  mq.addEventListener('change', apply)
}

/* ============================================================
 *  Toast 提示
 * ============================================================ */
function showToast(message, type = 'success') {
  if (toastTimer) clearTimeout(toastTimer)
  toast.value = { visible: true, message, type }
  toastTimer = setTimeout(() => { toast.value.visible = false }, 2200)
}

/* ============================================================
 *  一键粘贴
 * ============================================================ */
async function handlePaste() {
  try {
    const text = await navigator.clipboard.readText()
    if (text) {
      inputText.value = text.trim()
      inputRef.value?.focus()
      showToast('已粘贴', 'success')
    } else {
      showToast('剪贴板为空', 'error')
    }
  } catch {
    showToast('请使用 Ctrl+V 手动粘贴', 'error')
  }
}

/* ============================================================
 *  解析链接
 * ============================================================ */
async function handleParse() {
  const raw = inputText.value.trim()
  if (!raw) {
    shakeElement(inputRef.value)
    return
  }

  const lines = raw.split('\n').map(l => l.trim()).filter(Boolean)
  if (lines.length === 0) {
    shakeElement(inputRef.value)
    return
  }

  setLoading(true)

  try {
    const res = await fetch(API_BASE + '/parse', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: lines[0] }),
    })
    const data = await res.json()

    if (data.success) {
      currentResult.value = data.data
      errorMsg.value = ''
      showPreviewOverlay.value = true
      saveToHistory(data.data)
      renderHistory()
      resetProgress()
      // 滚动到结果区域
      await nextTick()
      const section = document.querySelector('#douyin-app .result-section')
      if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      showError(data.error || '未知错误')
    }
  } catch {
    showError('网络请求失败，请检查网络后重试')
  } finally {
    setLoading(false)
  }
}

/* ============================================================
 *  视频播放
 * ============================================================ */
function playVideo() {
  showPreviewOverlay.value = false
  videoPlayerRef.value?.play().catch(() => {})
}

/* ============================================================
 *  前端直连下载（优先），失败后降级为后端代理
 * ============================================================ */
async function handleDownload() {
  if (!currentResult.value) {
    showToast('请先解析视频链接', 'error')
    return
  }

  const videoUrl = currentResult.value.download_url
  const filename = (currentResult.value.title || currentResult.value.video_id) + '.mp4'

  resetProgress()
  downloadProgressVisible.value = true

  const proxyUrl = API_BASE + '/download?url=' + encodeURIComponent(videoUrl)
                    + '&filename=' + encodeURIComponent(filename)

  // 策略1：尝试前端直连（fetch 有进度 / <a> 标签兜底）
  try {
    const blob = await attemptDirectDownload(videoUrl, filename)
    if (blob) {
      triggerBlobDownload(blob, filename)
      updateProgress(100, '下载完成！')
      showToast('视频已开始下载', 'success')
      return
    }
    // blob 为 null 表示 <a> 标签已被触发
    updateProgress(100, '已交由浏览器下载')
    return
  } catch (err) {
    console.warn('直连下载失败，降级到代理：', err.message)
    updateProgress(0, '直连失败，切换到代理下载...')
  }

  // 策略2：降级为后端代理下载
  try {
    const blob = await attemptProxyDownload(proxyUrl, filename)
    if (blob) {
      triggerBlobDownload(blob, filename)
      updateProgress(100, '下载完成！')
      showToast('视频已开始下载', 'success')
      return
    }
  } catch (err) {
    console.error('所有下载方式均失败：', err)
    updateProgress(0, '下载失败')
  }

  // 最终兜底：新窗口打开代理链接
  window.open(proxyUrl, '_blank')
  showToast('正在打开下载窗口', 'error')
}

/**
 * 策略1：前端直连下载
 * A) fetch 直连（可跟踪进度，受 CORS 限制）
 * B) 失败时降级为 <a> 标签导航下载
 */
function attemptDirectDownload(url, filename) {
  updateProgress(null, '正在尝试直连下载...')

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 10000)

  return fetch(url, {
    method: 'GET',
    mode: 'cors',
    signal: controller.signal,
  }).then((res) => {
    clearTimeout(timeoutId)
    if (!res.ok || res.status === 403 || res.status === 404) throw new Error('HTTP ' + res.status)

    const contentLength = parseInt(res.headers.get('Content-Length') || '0', 10)
    updateProgress(null, '直连下载中...')
    return readStreamWithProgress(res.body, contentLength)
  }).catch((err) => {
    clearTimeout(timeoutId)
    console.info('直连获取失败（' + err.message + '），降级为 <a> 标签下载')

    updateProgress(null, '正在通过浏览器下载...')
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.target = '_blank'
    anchor.download = filename
    anchor.rel = 'noopener noreferrer'
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)

    showToast('已触发浏览器下载', 'success')
    return Promise.resolve(null)
  })
}

/**
 * 策略2：后端代理下载
 */
function attemptProxyDownload(proxyUrl, _filename) {
  updateProgress(null, '正在通过代理下载...')

  return fetch(proxyUrl, { method: 'GET' })
    .then((res) => {
      if (!res.ok) throw new Error('HTTP ' + res.status + ' (proxy)')
      const contentLength = parseInt(res.headers.get('Content-Length') || '0', 10)
      return readStreamWithProgress(res.body, contentLength)
    })
}

/**
 * 触发浏览器原生文件下载
 */
function triggerBlobDownload(blob, filename) {
  const blobUrl = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = blobUrl
  a.target = '_blank'
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000)
}

/**
 * 读取 ReadableStream 并跟踪进度
 */
function readStreamWithProgress(body, totalSize) {
  const reader = body.getReader()
  const chunks = []
  let received = 0

  return new Promise(function(resolve, reject) {
    function pump() {
      return reader.read().then(function(result) {
        if (result.done) {
          resolve(new Blob(chunks, { type: 'video/mp4' }))
          return
        }
        chunks.push(result.value)
        received += result.value.length
        if (totalSize > 0) {
          const percent = Math.min(Math.round(received / totalSize * 100), 99)
          const sizeStr = formatBytes(received) + ' / ' + formatBytes(totalSize)
          updateProgress(percent, '下载中... ' + sizeStr)
        } else {
          updateProgress(null, '下载中... ' + formatBytes(received))
        }
        return pump()
      })
    }
    pump().catch(reject)
  })
}

/* ============================================================
 *  进度管理
 * ============================================================ */
function updateProgress(percent, text) {
  if (percent !== null && percent !== undefined) progressPercent.value = percent
  if (text !== undefined) progressText.value = text
}

function resetProgress() {
  downloadProgressVisible.value = false
  progressPercent.value = 0
  progressText.value = ''
}

/* ============================================================
 *  复制链接
 * ============================================================ */
async function copyUrl() {
  const url = currentResult.value?.download_url
  if (!url) return
  try {
    await navigator.clipboard.writeText(url)
    showToast('链接已复制到剪贴板', 'success')
  } catch {
    const ta = document.createElement('textarea')
    ta.value = url
    ta.style.cssText = 'position:fixed;opacity:0'
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
    showToast('链接已复制', 'success')
  }
}

/* ============================================================
 *  表单操作
 * ============================================================ */
function showError(msg) {
  errorMsg.value = msg
  currentResult.value = null
  resetProgress()
}

function setLoading(active) {
  loading.value = active
  if (active) {
    const dots = ['...', '..', '.']
    let i = 0
    loadingTimer = setInterval(() => {
      loadingText.value = '\u6B63\u5728\u89E3\u6790\u89C6\u9891\u4FE1\u606F' + dots[i % 3]
      i++
    }, 500)
  } else {
    clearInterval(loadingTimer)
    loadingTimer = null
    loadingText.value = '\u6B63\u5728\u89E3\u6790\u89C6\u9891\u4FE1\u606F...'
  }
}

function resetForm() {
  inputText.value = ''
  inputRef.value?.focus()
  currentResult.value = null
  errorMsg.value = ''
  resetProgress()
}

function onInputKeydown(e) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    handleParse()
  }
}

/* ============================================================
 *  历史记录 — localStorage 存储
 * ============================================================ */
function getHistory() {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY)) || []
  } catch {
    return []
  }
}

function saveToHistory(info) {
  let history = getHistory()
  history = history.filter(item => item.video_id !== info.video_id)
  history.unshift({
    video_id: info.video_id,
    title: info.title,
    download_url: info.download_url,
    cover_url: info.cover_url || '',
    parsed_at: Date.now(),
  })
  if (history.length > MAX_HISTORY) history = history.slice(0, MAX_HISTORY)
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history))
}

function removeFromHistory(video_id) {
  const history = getHistory().filter(item => item.video_id !== video_id)
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history))
  renderHistory()
}

function clearHistory() {
  localStorage.removeItem(HISTORY_KEY)
  renderHistory()
}

function renderHistory() {
  historyList.value = getHistory()
}

function selectHistoryItem(item) {
  currentResult.value = item
  inputText.value = item.download_url
  showPreviewOverlay.value = true
  errorMsg.value = ''
  resetProgress()
}

/* ============================================================
 *  工具函数
 * ============================================================ */
function truncate(str, len) {
  return str.length > len ? str.slice(0, len) + '...' : str
}


function formatTime(timestamp) {
  if (!timestamp) return ''
  const d = new Date(timestamp)
  const now = new Date()
  const diffMs = now - d
  const diffMin = Math.floor(diffMs / 60000)
  const diffHour = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)
  if (diffMin < 1) return '\u521A\u521A'
  if (diffMin < 60) return diffMin + ' \u5206\u949F\u524D'
  if (diffHour < 24) return diffHour + '\u5C0F\u65F6\u524D'
  if (diffDay < 7) return diffDay + ' \u5929\u524D'
  return (d.getMonth() + 1) + '/' + d.getDate()
    + ' ' + String(d.getHours()).padStart(2, '0')
    + ':' + String(d.getMinutes()).padStart(2, '0')
}

function formatBytes(bytes) {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function shakeElement(el) {
  if (!el) return
  el.style.animation = 'none'
  void el.offsetWidth
  el.style.animation = 'shake 0.4s ease'
}
</script>

<template>
  <div id="douyin-app" :data-theme="theme">
    <!-- 返回首页 -->
    <button class="back-home" @click="goHome" title="返回首页">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7"/>
      </svg>
      返回首页
    </button>

    <div class="container">
      <!-- 头部 -->
      <header class="header">
        <h1 class="logo">抖音无水印下载</h1>
        <p class="subtitle">粘贴分享链接，获取无水印视频</p>
      </header>

      <!-- 输入区域 -->
      <section class="input-section">
        <div class="input-wrapper">
          <div class="textarea-box">
            <textarea
              v-model="inputText"
              placeholder="粘贴抖音分享链接，例如：https://v.douyin.com/xxxxx/&#10;支持同时粘贴多个链接（每行一个）"
              rows="3"
              autofocus
              ref="inputRef"
              @keydown="onInputKeydown"
            ></textarea>
          </div>
          <div class="btn-row">
            <button class="btn btn-paste" @click="handlePaste" title="从剪贴板粘贴">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
              </svg>
              一键粘贴
            </button>
            <button class="btn btn-primary" :disabled="loading" @click="handleParse">
              {{ loading ? '解析中...' : '解析链接' }}
            </button>
          </div>
        </div>
      </section>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <span>{{ loadingText }}</span>
      </div>

      <!-- 结果区域 -->
      <section v-if="showResultSection" class="result-section">
        <!-- 成功结果卡片 -->
        <div v-if="currentResult && !errorMsg" class="result-card">
          <!-- 视频预览播放器 -->
          <div class="video-preview">
            <video
              ref="videoPlayerRef"
              controls
              playsinline
              preload="metadata"
              :poster="currentResult.cover_url || ''"
              :src="proxyVideoSrc"
            >您的浏览器不支持视频播放</video>
            <div
              v-if="showPreviewOverlay"
              class="preview-overlay"
              @click="playVideo"
            >
              <svg class="play-icon-svg" viewBox="0 0 24 24" width="48" height="48">
                <polygon points="5,3 19,12 5,21" fill="#fff"/>
              </svg>
              <span class="preview-hint">点击预览视频</span>
            </div>
          </div>

          <!-- 作者信息栏 -->
          <div class="author-bar">
            <img
              v-if="currentResult.author?.avatar"
              :src="currentResult.author.avatar"
              class="author-avatar"
              alt=""
            />
            <div class="author-info">
              <span class="author-name">{{ currentResult.author?.name || '' }}</span>
              <span class="create-time">{{ currentResult.create_time || '' }}</span>
            </div>
          </div>

          <!-- 信息面板 -->
          <div class="result-body">
            <p class="info-value">{{ currentResult.title }}</p>
          </div>

          <!-- 操作栏 -->
          <div class="result-actions">
            <a class="btn btn-download" href="#" @click.prevent="handleDownload">
              下载视频
            </a>
            <button @click="copyUrl" class="btn btn-secondary">复制链接</button>
            <button @click="resetForm" class="btn btn-secondary">继续解析</button>
          </div>

          <!-- 下载进度条 -->
          <div v-if="downloadProgressVisible" class="download-progress">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>
            <p class="progress-text">{{ progressText }}</p>
          </div>
        </div>

        <!-- 错误提示 -->
        <div v-if="errorMsg" class="error-card">
          <p>{{ errorMsg }}</p>
          <button @click="resetForm" class="btn btn-primary">重新输入</button>
        </div>
      </section>

      <!-- 历史记录 -->
      <section v-if="historyList.length > 0" class="history-section">
        <div class="history-header">
          <h3>最近解析</h3>
          <button @click="clearHistory" class="btn-clear">清空</button>
        </div>
        <div class="history-list">
          <div
            v-for="item in historyList"
            :key="item.video_id"
            class="history-item"
            @click="selectHistoryItem(item)"
          >
            <div class="history-item-thumb">
              <img
                v-if="item.cover_url"
                :src="item.cover_url"
                alt=""
                class="history-cover"
              />
              <template v-else>&#9654;</template>
            </div>
            <div class="history-item-info">
              <div class="history-item-title">{{ truncate(item.title, 40) }}</div>
              <div class="history-item-time">{{ formatTime(item.parsed_at) }}</div>
            </div>
            <button
              class="history-item-action"
              title="删除记录"
              @click.stop="removeFromHistory(item.video_id)"
            >&times;</button>
          </div>
        </div>
      </section>

      <!-- 底部说明 -->
      <footer class="footer">
        <p>仅用于学习研究 · 请遵守相关法律法规</p>
      </footer>

      <!-- Toast -->
      <Transition name="toast">
        <div v-if="toast.visible" :class="['toast', 'toast-' + toast.type]">
          {{ toast.message }}
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
@keyframes shake {
  0%,100%{transform:translateX(0)}
  20%{transform:translateX(-8px)}
  40%{transform:translateX(8px)}
  60%{transform:translateX(-6px)}
  80%{transform:translateX(6px)}
}

.back-home {
  position: absolute;
  top: 16px;
  left: 16px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.18s ease;
  background: var(--bg-card);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  font-family: inherit;
  z-index: 10;
}

.back-home:hover {
  color: var(--text-primary);
  border-color: var(--text-tertiary);
  box-shadow: var(--shadow-sm);
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.25s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(16px);
}
</style>
